import { RefObject, useEffect, useState } from "react"

export interface RowWindow {
  start: number
  end: number
  topSpacer: number
  bottomSpacer: number
}

/**
 * Чистый расчёт окна виртуализации — вынесен для тестов.
 * Шапка находится ВНУТРИ того же скролл-контейнера, поэтому scrollTop включает её высоту,
 * и окно обязано её вычитать. rowCount — только сотрудники; строка «Итог» в окно не входит.
 */
export const computeRowWindow = (
  scrollTop: number,
  viewportHeight: number,
  rowCount: number,
  rowHeight: number,
  headerHeight: number,
  overscan: number,
): RowWindow => {
  const contentTop = Math.max(0, scrollTop - headerHeight)
  const visible = Math.max(0, viewportHeight - headerHeight)
  // Клампим видимое окно ДО overscan: scrollTop может кратно превысить контент,
  // но start никогда не должен оказаться больше end (иначе slice даст пустоту,
  // а спейсеры станут выше всех строк вместе).
  const firstVisible = Math.min(rowCount, Math.floor(contentTop / rowHeight))
  const lastVisible = Math.min(
    rowCount,
    Math.ceil((contentTop + visible) / rowHeight),
  )
  const start = Math.max(0, firstVisible - overscan)
  const end = Math.max(start, Math.min(rowCount, lastVisible + overscan))
  return {
    start,
    end,
    topSpacer: start * rowHeight,
    bottomSpacer: (rowCount - end) * rowHeight,
  }
}

interface UseRowWindowArgs {
  scrollRef: RefObject<HTMLElement>
  rowHeight: number
  rowCount: number
  headerHeight: number
  overscan: number
}

/**
 * Окно виртуализации строк одного скролл-контейнера. Пересчёт дросселируется через rAF:
 * событие scroll летит чаще кадра, а каждый пересчёт — это ререндер всех видимых строк.
 */
export const useRowWindow = ({
  scrollRef,
  rowHeight,
  rowCount,
  headerHeight,
  overscan,
}: UseRowWindowArgs): RowWindow => {
  const [rowWindow, setRowWindow] = useState<RowWindow>(() =>
    computeRowWindow(0, 0, rowCount, rowHeight, headerHeight, overscan),
  )

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let raf = 0
    const update = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() =>
        setRowWindow(
          computeRowWindow(
            el.scrollTop,
            el.clientHeight,
            rowCount,
            rowHeight,
            headerHeight,
            overscan,
          ),
        ),
      )
    }
    update()
    el.addEventListener("scroll", update, { passive: true })
    // ResizeObserver есть во всех целевых браузерах, но НЕ в jsdom 23 — под тестами
    // компонентов грида его отсутствие не должно ронять монтирование.
    const ro =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update)
    ro?.observe(el)
    return () => {
      el.removeEventListener("scroll", update)
      ro?.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [scrollRef, rowHeight, rowCount, headerHeight, overscan])

  return rowWindow
}
